import { useEffect, useRef, useState } from "react";
import { Head } from "vite-react-ssg";
import { Code, Cpu, Layers, MessageSquare } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

const HERO_VIDEO_SRC = "/lovable-uploads/video_1751292840840_1751292842546.mp4";

const Hero = () => {
  const isMobile = useIsMobile();
  // The poster paints immediately; the video only starts downloading after
  // hydration so it never competes with the LCP on slow connections.
  const [videoSrc, setVideoSrc] = useState<string>();
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    setVideoSrc(HERO_VIDEO_SRC);
  }, []);
  useEffect(() => {
    // Setting src after mount doesn't always retrigger autoplay; nudge it.
    if (videoSrc) videoRef.current?.play().catch(() => {});
  }, [videoSrc]);
  // Solo transform (sin opacity): el texto del hero es el LCP y un fade-in lo
  // dejaría invisible en el HTML prerenderizado hasta que hidrata el JS.
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
        duration: 0.8
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20
    },
    visible: {
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact-info');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  return <motion.div className="relative w-full" initial="hidden" animate="visible" variants={containerVariants}>
    {/* El póster es el LCP de la home: precárgalo con prioridad alta. */}
    <Head>
      <link rel="preload" as="image" href="/hero-consulting.webp" fetchPriority="high" />
    </Head>
    <div className="banner-container bg-slate-800 relative overflow-hidden h-[90vh] sm:h-[80vh] md:h-[500px] lg:h-[550px] xl:h-[600px] w-full">
      <div className="absolute inset-0 bg-slate-800 w-full">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          src={videoSrc}
          className={`w-full h-full object-cover opacity-90 hero-video ${isMobile ? 'object-right' : 'object-center'}`}
          poster="/hero-consulting.webp"
        >
          {/* Fallback image if video fails to load */}
          <img
            src="/lovable-uploads/4bfa0d71-3ed2-4693-90b6-35142468907f.png"
            alt="D&J Partners"
            className={`w-full h-full object-cover opacity-80 ${isMobile ? 'object-right' : 'object-center'}`}
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800/80 via-slate-800/70 to-white"></div>
      </div>

      <div className="banner-overlay bg-transparent pt-8 sm:pt-16 md:pt-32 pb-16 sm:pb-20 w-full h-full flex items-center">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center h-full">
          <motion.div className="w-full max-w-4xl text-center" variants={itemVariants}>
            <motion.h1 className="banner-title text-white" variants={itemVariants}>Transformamos empresas con Inteligencia Artificial aplicada al negocio.</motion.h1>
            <motion.p className="banner-subtitle text-gray-300 mt-4 sm:mt-6" variants={itemVariants}>
              Ayudamos a PYMEs a crecer y competir mediante soluciones de IA que optimizan procesos, mejoran decisiones y automatizan tareas repetitivas.
            </motion.p>
            <motion.div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8 justify-center items-center" variants={itemVariants}>
              {/* Styled as a button but using an anchor tag for project navigation */}
              {/* <button 
                  className="w-full sm:w-auto min-h-[44px] px-6 sm:px-8 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-all shadow-lg hover:shadow-xl hover:shadow-gray-300/20 flex items-center justify-center group text-sm sm:text-base font-medium"
                  onClick={e => {
                    e.preventDefault();
                    const projectsSection = document.getElementById('projects');
                    if (projectsSection) {
                      projectsSection.scrollIntoView({
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  Ver Casos de Éxito
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </button> */}

              {/* Baja a la sección de contacto (agendar reunión o escribir) */}
              <button
                onClick={scrollToContact}
                className="w-full sm:w-auto min-h-[44px] px-6 sm:px-8 py-3 bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl hover:shadow-slate-300/20 flex items-center justify-center group text-sm sm:text-base font-medium"
              >
                Solicita una Consultoría Gratuita
                <MessageSquare className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>

    <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 mx-auto">
      <motion.div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" variants={containerVariants} initial="hidden" animate="visible" transition={{
        delay: 0.6
      }}>
        <motion.div className="bg-white p-4 md:p-5 rounded-xl shadow-sm border border-gray-100 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md" variants={itemVariants}>
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-100 flex items-center justify-center rounded-lg text-gray-500 mb-2 md:mb-3">
            <Cpu className="w-5 h-5 md:w-6 md:h-6" />
          </div>
          <h2 className="text-base md:text-lg font-semibold mb-1 md:mb-2 text-gray-800">Implementación de IA</h2>
          <p className="text-gray-600 text-xs md:text-sm">Desarrollo e integración estratégica de soluciones IA adaptadas a procesos empresariales críticos.</p>
        </motion.div>

        <motion.div className="bg-white p-4 md:p-5 rounded-xl shadow-sm border border-gray-100 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md" variants={itemVariants}>
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-100 flex items-center justify-center rounded-lg text-gray-500 mb-2 md:mb-3">
            <Code className="w-5 h-5 md:w-6 md:h-6" />
          </div>
          <h2 className="text-base md:text-lg font-semibold mb-1 md:mb-2 text-gray-800">BI y Dashboards Personalizados</h2>
          <p className="text-gray-600 text-xs md:text-sm">Transformamos datos complejos en insights visuales y accionables para líderes de negocio.</p>
        </motion.div>

        <motion.div className="bg-white p-4 md:p-5 rounded-xl shadow-sm border border-gray-100 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md" variants={itemVariants}>
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-100 flex items-center justify-center rounded-lg text-gray-500 mb-2 md:mb-3">
            <Layers className="w-5 h-5 md:w-6 md:h-6" />
          </div>
          <h2 className="text-base md:text-lg font-semibold mb-1 md:mb-2 text-gray-800">Inteligencia de Negocio</h2>
          <p className="text-gray-600 text-xs md:text-sm">Convertimos tus datos en decisiones inteligentes que impulsan el crecimiento y la rentabilidad de tu PYME.</p>
        </motion.div>
      </motion.div>
    </div>
  </motion.div>;
};

export default Hero;
